//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { ProductDetailsModalContent } from '@campaigns/components/shared/product-details-modal/modal/ProductDetailsModalContent';
import {
  SAMPLING_CAMPAIGNS_TRACKING_ID,
  SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX,
} from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  ProductModalMode,
  useProductModal,
} from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import {
  isMissingDetails,
  prepareProductToSubmit,
} from '@campaigns/utils/sampling/SamplingProductUtils';
import { PrimaryButton, TertiaryButton } from '@components/atoms/button/Button';
import { RightDrawer } from '@components/molecules/right-drawer/RightDrawer';

// New product selection experience, gated behind SamplingCampaignsProductSelectionRework.
// Reuses the existing tab content for now; look and feel will be reworked in follow-up PRs.
export const ProductDetailsDrawer = () => {
  const {
    addProductToCampaign,
    closeProductDetailsModal,
    isProductDetailsModalOpen,
    productModalState,
    uploadImageFilesFromProduct,
  } = useProductModal();

  const { action, mode, product } = productModalState;
  const isViewMode = mode === ProductModalMode.View;
  const isEditMode = mode === ProductModalMode.Edit;
  const hasRequiredDetails = !isMissingDetails(product);

  const methods = useForm<EditableBVSamplingProduct>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      ...productModalState.product,
      // Only set newProduct to true if in create mode or if explicitly set to true on the product
      newProduct:
        mode === ProductModalMode.Create ||
        productModalState.product?.newProduct,
      variations: productModalState.product?.variations ?? [],
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialValidationTriggered, setHasInitialValidationTriggered] =
    useState(false);

  // Prevent closing (via header button, outside click, or Escape) while the submit is in flight
  const handleCloseDrawer = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    closeProductDetailsModal();
  }, [closeProductDetailsModal, isSubmitting]);

  const handleProductUpdate = useCallback(
    (productToSubmit: BVSamplingProduct) => {
      const isProductMissingDetails = isMissingDetails(productToSubmit);

      if (isProductMissingDetails) {
        return;
      }

      if (addProductToCampaign) {
        addProductToCampaign(
          productToSubmit,
          action === ProductModalMode.Edit,
          productModalState.originalProductId,
        );
      }

      closeProductDetailsModal();
    },
    [closeProductDetailsModal, addProductToCampaign, action, productModalState],
  );

  const onSubmit = useCallback(
    async (product: EditableBVSamplingProduct) => {
      setIsSubmitting(true);
      try {
        // Note: toast notification on failure is shown by useUploadProductImagesMutation;
        // catch here only to stop the rejection from propagating as unhandled
        const dataWithUploadedImages = await uploadImageFilesFromProduct(
          product,
        ).catch(() => undefined);

        if (!dataWithUploadedImages) {
          return;
        }

        const preparedProduct = prepareProductToSubmit(dataWithUploadedImages);
        handleProductUpdate(preparedProduct);
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleProductUpdate, uploadImageFilesFromProduct],
  );

  const shouldTriggerValidation =
    isEditMode && !hasRequiredDetails && !hasInitialValidationTriggered;

  useEffect(() => {
    if (shouldTriggerValidation) {
      setHasInitialValidationTriggered(true);
      void methods.handleSubmit(onSubmit)();
    }
  }, [shouldTriggerValidation, methods, onSubmit]);

  const getDrawerTitle = () => {
    if (isViewMode) {
      return 'View Product';
    }

    return mode === ProductModalMode.Create
      ? 'Create New Product'
      : 'Edit Product';
  };

  return (
    <FormProvider {...methods}>
      <RightDrawer
        closeDrawer={handleCloseDrawer}
        isDrawerOpen={isProductDetailsModalOpen}
        shouldCloseOnClickOutside={false}
        shouldCloseOnEsc={false}
        trackingIdPrefix={SAMPLING_CAMPAIGN_PRODUCT_DRAWER_PREFIX}
        width='extra-wide'
      >
        <RightDrawer.Header>
          <RightDrawer.HeaderTitleGroup>
            <RightDrawer.HeaderTitle>
              {getDrawerTitle()}
            </RightDrawer.HeaderTitle>
          </RightDrawer.HeaderTitleGroup>
          <RightDrawer.HeaderClose
            isDisabled={isSubmitting}
            onClick={handleCloseDrawer}
          />
        </RightDrawer.Header>
        <RightDrawer.Body>
          <ProductDetailsModalContent isViewOnly={isViewMode} />
        </RightDrawer.Body>
        <RightDrawer.Footer>
          <RightDrawer.FooterActionBar>
            {isViewMode ? (
              <PrimaryButton
                onClick={handleCloseDrawer}
                trackingId={
                  SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON
                }
              >
                Close
              </PrimaryButton>
            ) : (
              <>
                <TertiaryButton
                  isDisabled={isSubmitting}
                  onClick={handleCloseDrawer}
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CANCEL_BUTTON
                  }
                >
                  Cancel
                </TertiaryButton>
                <PrimaryButton
                  isDisabled={isSubmitting}
                  onClick={methods.handleSubmit(onSubmit)}
                  trackingId={
                    SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_SUBMIT_BUTTON
                  }
                >
                  {mode === ProductModalMode.Create
                    ? 'Create Product'
                    : 'Save Product'}
                </PrimaryButton>
              </>
            )}
          </RightDrawer.FooterActionBar>
        </RightDrawer.Footer>
      </RightDrawer>
    </FormProvider>
  );
};
